// src/components/business/BusinessHealthBar.tsx
// Exact replica of mobile BusinessHealthBar.tsx
import React, { useState, useEffect } from 'react';
import { TrendingUp, CheckCircle, AlertTriangle, AlertCircle, XCircle, Loader2 } from 'lucide-react';
import apiClient from '@/services/apiClient';

type TimePeriod = 'hours' | 'days' | 'months' | 'years';

interface HealthData {
  label: string;
  value: number;
  color: [string, string]; // gradient [from, to]
}

interface Props { businessId: string; }

const getHealthGradient = (value: number): [string, string] => {
  if (value >= 80) return ['#10B981', '#34D399'];
  if (value >= 60) return ['#3B82F6', '#60A5FA'];
  if (value >= 40) return ['#F59E0B', '#FBBF24'];
  if (value >= 20) return ['#EF4444', '#F87171'];
  return ['#6B7280', '#9CA3AF'];
};

const getHealthStatus = (avg: number) => {
  if (avg >= 80) return { text: 'Excellent', color: '#10B981', Icon: TrendingUp };
  if (avg >= 60) return { text: 'Good',      color: '#3B82F6', Icon: CheckCircle };
  if (avg >= 40) return { text: 'Fair',      color: '#F59E0B', Icon: AlertTriangle };
  if (avg >= 20) return { text: 'Poor',      color: '#EF4444', Icon: AlertCircle };
  return           { text: 'Critical',  color: '#6B7280', Icon: XCircle };
};

const periodLabel: Record<TimePeriod, string> = {
  hours: 'Last 24 Hours', days: 'This Week', months: 'This Year', years: 'Last 5 Years',
};

function generateHealthData(period: TimePeriod, base: number): HealthData[] {
  const now = new Date();
  const h = now.getHours(), day = now.getDay(), month = now.getMonth(), year = now.getFullYear();
  const startHealth = (offset: number) => Math.max(5, base - offset);
  const lerp = (i: number, total: number, start: number) =>
    Math.round(start + (base - start) * (total > 0 ? i / total : 1));

  switch (period) {
    case 'hours': {
      const s = startHealth(30);
      return Array.from({ length: h + 1 }, (_, i) => {
        const v = lerp(i, h, s);
        return { label: i < 10 ? `0${i}` : `${i}`, value: v, color: getHealthGradient(v) };
      });
    }
    case 'days': {
      const labels = ['S','M','T','W','T','F','S'];
      const s = startHealth(15);
      if (day === 0) {
        return [1,2,3,4,5,6,0].map((d, i) => {
          const v = lerp(i, 6, s);
          return { label: labels[d], value: v, color: getHealthGradient(v) };
        });
      }
      return Array.from({ length: day }, (_, i) => {
        const v = lerp(i, day - 1, s);
        return { label: labels[i + 1], value: v, color: getHealthGradient(v) };
      });
    }
    case 'months': {
      const mLabels = ['J','F','M','A','M','J','J','A','S','O','N','D'];
      const s = startHealth(25);
      return Array.from({ length: month + 1 }, (_, i) => {
        const v = lerp(i, month, s);
        return { label: mLabels[i], value: v, color: getHealthGradient(v) };
      });
    }
    case 'years':
      return [{ label: `'${String(year).slice(2)}`, value: base, color: getHealthGradient(base) }];
  }
}

const BusinessHealthBar: React.FC<Props> = ({ businessId }) => {
  const [period, setPeriod] = useState<TimePeriod>('days');
  const [data, setData] = useState<HealthData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`/businesses/products/${businessId}/inventory-stats`);
        const s = res.data.summary;
        const products: any[] = res.data.products || [];
        const total = s.total_products || 0;
        const active = s.active_products || 0;
        const withSales = products.filter(p => (p.total_sold || 0) > 0).length;
        const revenue = products.reduce((sum, p) => sum + (p.total_revenue || 0), 0);
        const diversity = Math.min((total / 20) * 100, 100);
        const sales = total > 0 ? (withSales / total) * 100 : 0;
        const rev = revenue > 0 ? Math.min((revenue / 10000) * 100, 100) : 0;
        const stock = total > 0 ? ((active - (s.low_stock_products || 0)) / total) * 100 : 0;
        const avg = diversity * 0.25 + sales * 0.35 + rev * 0.25 + stock * 0.15;
        if (!cancelled) setData(generateHealthData(period, Math.round(avg)));
      } catch {
        if (!cancelled) setData(generateHealthData(period, 10));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [period, businessId]);

  const avg  = data.length ? Math.round(data.reduce((s, d) => s + d.value, 0) / data.length) : 0;
  const peak = data.length ? Math.max(...data.map(d => d.value)) : 0;
  const low  = data.length ? Math.min(...data.map(d => d.value)) : 0;
  const status = getHealthStatus(avg);

  return (
    // mirrors mobile: marginHorizontal 16, marginVertical 12, paddingHorizontal 20, paddingVertical 16
    <div className="mx-4 my-3 px-5 py-4 rounded-2xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border">

      {/* Header — title left, period toggle right */}
      <div className="flex items-start justify-between mb-2">
        <div>
          {/* title: fontSize 20, fontWeight bold, color #006884 */}
          <h3 className="text-xl font-bold" style={{ color: '#006884' }}>Business Health</h3>
          {!loading && (
            // statusContainer: row, gap 6
            <div className="flex items-center gap-1.5 mt-1.5">
              <status.Icon size={16} style={{ color: status.color }} />
              {/* statusText: fontSize 13, fontWeight 600 */}
              <span className="text-[13px] font-semibold" style={{ color: status.color }}>
                {status.text} ({avg}%)
              </span>
            </div>
          )}
        </div>

        {/* periodToggleContainer: bg #F1F5F9, borderRadius 8, padding 3 */}
        <div className="flex items-center gap-0.5 rounded-lg p-0.5" style={{ backgroundColor: '#F1F5F9' }}>
          {(['hours','days','months','years'] as TimePeriod[]).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              // periodButton: px 10, py 6, borderRadius 6, minWidth 32
              // periodButtonActive: bg #006884
              className="px-2.5 py-1.5 rounded-md min-w-[32px] text-center transition-colors"
              style={period === p
                ? { backgroundColor: '#006884', color: '#fff' }
                : { color: '#64748B' }
              }
            >
              {/* periodButtonText: fontSize 12, fontWeight 700 */}
              <span className="text-[12px] font-bold">
                {p === 'hours' ? 'H' : p === 'days' ? 'D' : p === 'months' ? 'M' : 'Y'}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* subtitle: fontSize 12, color #64748B, fontWeight 500 */}
      <p className="text-[12px] font-medium mb-4" style={{ color: '#64748B' }}>
        {periodLabel[period]}
      </p>

      {loading ? (
        <div className="flex justify-center py-5">
          <Loader2 size={28} className="animate-spin" style={{ color: '#006884' }} />
        </div>
      ) : (
        <>
          {/* labelsRow: row, justifyContent space-between, mb 10 */}
          <div className="flex justify-between mb-2.5 px-0.5">
            {data.map((d, i) => (
              <div key={i} className="flex-1 text-center">
                {/* labelText: fontSize 11, fontWeight 700, color #475569 */}
                {/* hourLabelText: fontSize 8 */}
                <span
                  className="font-bold text-center"
                  style={{
                    fontSize: period === 'hours' ? 8 : 11,
                    color: '#475569',
                  }}
                >
                  {d.label}
                </span>
              </div>
            ))}
          </div>

          {/* healthBarContainer: row, height 80, alignItems flex-end, gap 1 */}
          <div className="flex items-end gap-px mb-4" style={{ height: 80 }}>
            {data.map((d, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm"
                style={{
                  height: `${Math.max(d.value, 5)}%`,
                  minHeight: 4,
                  // LinearGradient: start {x:0,y:0} end {x:0,y:1} → top to bottom
                  background: `linear-gradient(to bottom, ${d.color[0]}, ${d.color[1]})`,
                }}
              />
            ))}
          </div>

          {/* quickStats: row, justifyContent space-around, py 12, px 8, bg #F8FAFC, borderRadius 12, mb 12 */}
          <div
            className="flex items-center justify-around py-3 px-2 rounded-xl mb-3"
            style={{ backgroundColor: '#F8FAFC' }}
          >
            {[
              { label: 'Peak',    value: `${peak}%` },
              { label: 'Average', value: `${avg}%`  },
              { label: 'Lowest',  value: `${low}%`  },
            ].map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 && (
                  // statDivider: width 1, height 24, bg #CBD5E1
                  <div className="w-px h-6" style={{ backgroundColor: '#CBD5E1' }} />
                )}
                <div className="flex-1 text-center">
                  {/* statLabel: fontSize 11, color #64748B, mb 4, fontWeight 500 */}
                  <p className="text-[11px] font-medium mb-1" style={{ color: '#64748B' }}>{s.label}</p>
                  {/* statValue: fontSize 16, fontWeight bold, color #006884 */}
                  <p className="text-base font-bold" style={{ color: '#006884' }}>{s.value}</p>
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* infoText: fontSize 11, color #94A3B8, fontStyle italic, lineHeight 16, textAlign center */}
          <p className="text-[11px] italic text-center leading-4" style={{ color: '#94A3B8' }}>
            Health score is calculated based on sales activity, inventory turnover,
            customer engagement, and revenue performance.
          </p>
        </>
      )}
    </div>
  );
};

export default BusinessHealthBar;
